import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { Avatar, Box, Button, Chip, Grid, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useState } from 'react';
import moment from 'moment';
import i18n from 'app/i18n';
import { useTranslation } from 'react-i18next';
import { useGetSensorTypesQuery, useGetUserExternalDevicesQuery, useGetUserLastValuesQuery, useGetUsersQuery } from 'features/api/apiSlice';
import MainCard from 'components/MainCard';
import { getDeviceBrandName, getSensorTypeName } from 'helpers/ExternalDeviceHelper';
import { useAppSelector } from 'app/hooks';
import { selectCurrentUser } from 'features/auth/authSlice';
import { IExternalDevice } from 'models/IExternalDevice';
import { IDeviceDataExtended } from 'models/IUser';

interface ProfileTabProps {
  userId: string;
}

function ProfileTabSkeleton() {
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <MainCard title="User Information">
        <Grid container spacing={2}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Stack spacing={1}>
                <Box sx={{ width: '40%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                <Box sx={{ width: '85%', height: 26, borderRadius: 1, bgcolor: 'action.hover' }} />
              </Stack>
            </Grid>
          ))}
        </Grid>
      </MainCard>

      <MainCard title="Devices Status">
        <Grid container spacing={1.25}>
          {[1, 2].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'action.hover' }} />
                    <Stack spacing={0.75}>
                      <Box sx={{ width: 140, height: 18, borderRadius: 1, bgcolor: 'action.hover' }} />
                      <Box sx={{ width: 120, height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                    </Stack>
                  </Box>
                  <Box sx={{ width: 72, height: 24, borderRadius: 20, bgcolor: 'action.hover' }} />
                </Box>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {[1, 2, 3].map((row) => (
                    <Grid container spacing={1} key={row} sx={{ py: 0.75 }}>
                      <Grid item xs={5}>
                        <Box sx={{ width: '85%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ width: '90%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                      </Grid>
                      <Grid item xs={3}>
                        <Box sx={{ width: '80%', height: 14, borderRadius: 1, bgcolor: 'action.hover', ml: 'auto' }} />
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </MainCard>
    </Stack>
  );
}

export default function ProfileTab({ userId }: ProfileTabProps) {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectCurrentUser);
  const [copiedUserId, setCopiedUserId] = useState(false);
  const { data: sensorTypes = [] } = useGetSensorTypesQuery();
  const { data: users = [], isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery(
    authUser?.id ? { parentId: authUser.id, role: null } : skipToken
  );
  const { data: userLastValues = [], isLoading: isLoadingLastValues, isFetching: isFetchingLastValues } = useGetUserLastValuesQuery(
    userId ? userId : skipToken
  );
  const { data: externalDevices = [], isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetUserExternalDevicesQuery(
    userId ? userId : skipToken
  );

  const patient = users.find((user) => user.id === userId);
  const isLoadingProfileData =
    isLoadingUsers ||
    isLoadingLastValues ||
    isLoadingExternalDevices ||
    isFetchingUsers ||
    isFetchingLastValues ||
    isFetchingExternalDevices;

  if (isLoadingProfileData) {
    return <ProfileTabSkeleton />;
  }

  if (!patient) {
    return (
      <Stack spacing={2} sx={{ mt: 1 }}>
        <MainCard title="User Information">
          <Typography color="text.secondary">{t('noData', 'No data available')}</Typography>
        </MainCard>
      </Stack>
    );
  }

  const connectedDevices = externalDevices.filter((device) => device.assignedUserId === patient.id);
  const normalized = (value?: string | null) => (value || '').trim().toLowerCase();
  const formattedEnrolment = patient.enrolmentAt ? moment(patient.enrolmentAt.toString()).locale(i18n.language).format('l') : '—';

  const handleCopyUserId = async () => {
    if (!patient.id) return;

    await navigator.clipboard.writeText(patient.id);
    setCopiedUserId(true);
    setTimeout(() => setCopiedUserId(false), 2000);
  };

  const latestValuesByDevice = new Map<string, IDeviceDataExtended>();
  userLastValues.forEach((device) => {
    const keys = [device.id, device.brandId, device.name].map(normalized).filter(Boolean);
    keys.forEach((key) => {
      if (!latestValuesByDevice.has(key)) {
        latestValuesByDevice.set(key, device);
      }
    });
  });

  const devicesWithLatestValues = connectedDevices.map((device) => {
    const deviceKeys = [device.id, device.brandId, device.name].map(normalized).filter(Boolean);
    const latestValues = deviceKeys.map((key) => latestValuesByDevice.get(key)).find(Boolean);
    return { device, latestValues };
  });

  const getDeviceInitials = (deviceName?: string | null) => {
    const cleanName = (deviceName || '').trim();
    if (!cleanName) return '?';

    const parts = cleanName.split(/\s+/);
    if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  };

  const deviceColumns = devicesWithLatestValues.length > 1 ? 6 : 12;

  const getDeviceDisplayName = (device: IExternalDevice) => {
    const brandId = device.brandId || '';
    const label = getDeviceBrandName(brandId);
    return label || 'Unknown device';
  };

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <MainCard title="User Information">
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              User ID
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  fontFamily: 'monospace',
                  maxWidth: 260,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {patient.id || '—'}
              </Typography>

              <Button
                variant="text"
                size="small"
                onClick={handleCopyUserId}
                disabled={!patient.id}
                sx={{ minWidth: 36, px: 1 }}
              >
                {copiedUserId ? <CheckOutlined /> : <CopyOutlined />}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }} noWrap>
              {patient.email || '—'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              Enrolment
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }} noWrap>
              {formattedEnrolment}
            </Typography>
          </Grid>
        </Grid>
      </MainCard>

      <MainCard title="Devices Status">

        <Grid container spacing={1.25}>
          {devicesWithLatestValues.length === 0 ? (
            <Grid item xs={12}>
              <Typography color="text.secondary">{t("noData", "No data available")}</Typography>
            </Grid>
          ) : (
            devicesWithLatestValues.map(({ device, latestValues }) => {
              const deviceDisplayName = getDeviceDisplayName(device);
              let statusChip: JSX.Element | null = null;

              switch (device.status) {
                case 'revoked':
                  statusChip = <Chip color="error" label="Revoked" size="small" variant="light" />;
                  break;
                case 'expired':
                  statusChip = <Chip color="warning" label="Expired" size="small" variant="light" />;
                  break;
                case 'active':
                  statusChip = <Chip color="success" label="Active" size="small" variant="light" />;
                  break;
                default:
                  statusChip = device.status ? <Chip color="info" label={device.status} size="small" variant="light" /> : null;
              }

              return (
              <Grid item xs={12} md={deviceColumns} key={device.id ?? device.brandId ?? device.name ?? 'device'}>
                <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        fontSize: 18,
                        fontWeight: 500,
                        bgcolor: 'common.white',
                        color: 'common.black',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                        {getDeviceInitials(deviceDisplayName)}
                    </Avatar>

                    <Box>
                        <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
                          {deviceDisplayName}
                        </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
                        {device.lastSyncedAt && moment(device.lastSyncedAt).isValid() ? `Last synced: ${moment(device.lastSyncedAt).locale(i18n.language).fromNow()}` : 'Last synced: —'}
                      </Typography>
                    </Box>
                  </Box>

                  {statusChip}
                </Box>

                {!latestValues ? (
                  <Typography color="text.secondary">No latest values available.</Typography>
                ) : latestValues.sensorsDataExtended.length === 0 ? (
                  <Typography color="text.secondary">{t("noData", "No data available")}</Typography>
                ) : (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <Table size="small" aria-label="latest sensor values">
                      <TableBody>
                        {latestValues.sensorsDataExtended.map((sensor) => (
                          <TableRow key={`${latestValues.id}-${sensor.typeId}`}>
                            <TableCell sx={{ py: 0.75 }}>{getSensorTypeName(sensor.typeId, sensorTypes, t)}</TableCell>
                            <TableCell sx={{ py: 0.75, color: 'text.secondary' }}>
                              {sensor.lastValueAt && moment(sensor.lastValueAt).isValid() ? moment(sensor.lastValueAt).locale(i18n.language).format('l LTS') : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.75 }}>
                              {`${sensor.lastValue}${sensor.unit ? ` ${sensor.unit}` : ''}`}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
              </Grid>
              );
            })
          )}
        </Grid>
      </MainCard>
    </Stack>
  );
}
